import { env } from '@/config/environment';
import { useLaunchViewTrace } from '@/diag/launchTrace';
// 20260920 - THE DOCK READOUT BELOW. Both are LEAF modules by design (platform/foldStateStore.ts
// explains why the fold state is not a uiStore slice), so the badge gains no edge to the bridge,
// the project store or i18n by reading them — which is the only reason a diagnostic is allowed to
// sit in a component this early in the tree.
import { useSideDock } from '@/hooks/useSideDock';
import { useFoldState } from '@/platform/foldStateStore.ts';
import styles from './DevBanner.module.css';

/**
 * Dev-environment badge: a fixed, pointer-events:none overlay centred on the bottom edge.
 * 20260922 (owner): moved there from the top-left corner "so it clashes less with important ui" —
 * top-left it sat over the board bar's home button and, on a phone, the tab strip; bottom-centre
 * it shares its band only with the Timeline's transport. Same layering and the same safe-area
 * aliases as before (DevBanner.module.css); only the anchor changed.
 *
 * FREE-FLOATING BY OWNER RULING (2026-08-20). It may overlap whatever is behind it — the board
 * bar's home button, the iPadOS window-controls pill — and nothing in the app repositions itself
 * around it. It used to MEASURE its own width into a `--dev-banner-clearance` custom property on
 * <html> (a ResizeObserver, because the badge prints the Supabase project ref and so its width is
 * environment- and font-dependent) which the board shell's home button consumed as a margin-left.
 * That coupling is deleted: stacked on the bar's own leading inset it pushed the home button far
 * off the left edge, and the bar reserving space for a debug overlay was the wrong model in the
 * first place. Do not reintroduce it — SceneBoardPage.test.tsx pins the absence on both sides.
 *
 * Positioning is likewise position-invariant across platforms: DevBanner.module.css carries no
 * @media block at all, and that absence is pinned too.
 */
export function DevBanner() {
  if (!env.isDev) return null;
  // The avatar customizer (?page=avatar) is a self-contained embedded panel with no external (Supabase /
  // Vercel) calls, so a Dev/Prod indicator is meaningless there — suppress it on that page only.
  if (new URLSearchParams(window.location.search).get('page') === 'avatar') return null;
  // 20260928 - The canvas overlay (?page=canvas-overlay) is a TRANSPARENT page over Unity's 3D canvas,
  // up only while its hamburger / Scene info / Shot info / Preferences is: the pill would float over
  // the canvas for exactly those moments, beside Unity's own DEV badge. Unity's badge already says
  // which environment this build talks to.
  if (new URLSearchParams(window.location.search).get('page') === 'canvas-overlay') return null;
  return <DevBadge />;
}

/** Split from the gate above purely to keep the badge's own markup separate from the two early
 *  returns (env.isDev is fixed at module load, but the page param is read live on every render). */
function DevBadge() {
  // Show first 6 chars of supabase project ref so Dev/Prod is unambiguous
  let shortRef = '';
  try {
    shortRef = new URL(env.supabaseUrl).hostname.split('.')[0].slice(0, 6);
  } catch {}

  // [LAUNCH VIEW] The BOTTOM-CENTRE orange DEV label (top-left until 20260922). Unity has its own,
  // bottom-right and DontDestroyOnLoad (Assets/Scripts/UI/DevBadge.cs) — the owner's "the dev label
  // moved from the lower right to the upper left" was those two labels handing over as a native
  // WebView covers the Unity UI, not one label relocating. Traced here so the log shows them as two
  // separate things. Below both early returns, so a page that suppresses the badge traces nothing.
  useLaunchViewTrace('DevBanner', 'corner=bottom-center');

  return (
    <div className={styles.banner} role="status" aria-label="Development environment">
      DEV{shortRef ? ` — ${shortRef}` : ''} · <DockReadout />
    </div>
  );
}

/**
 * 20260920 - WHAT THE SIDE DOCK ACTUALLY RESOLVED, in the badge, because the alternative is
 * inferring it from panel margins in a screenshot.
 *
 * WHY IT EARNS ITS PLACE. The dock's geometry is decided by four ranked sources and two
 * inferences, and every symptom of getting it wrong looks like a layout bug: a column on the wrong
 * edge reads as "the dock is drawn over the panel", a stale source reads as "the page moved but the
 * dock did not", a housing end reads as "the buttons are too high". Two rounds of this batch were
 * spent reading margins out of photographs, and one of those readings was wrong. The owner iterates
 * by screenshot; this makes the next screenshot decisive.
 *
 * READ FROM THE SAME PLACES THE DOCK READS, and that is the whole contract: useSideDock() is
 * literally what SceneBoardPage and ProjectsView hand to <SideDock>, and useFoldState() is the
 * resolution it comes out of. A readout with a source of its own could disagree with the thing it
 * is describing, which would be worse than no readout at all.
 *
 * DEV ONLY, THROUGH THE EXISTING GATE. This is rendered from DevBadge, which DevBanner returns only
 * after `env.isDev` — the same single gate the badge has always had. There is deliberately NO
 * second condition here: a diagnostic with its own switch is a diagnostic that can be left on.
 *
 * SHORT, AND NEVER WRAPPED. Three fields, slash-separated, on a badge that is already
 * `white-space: nowrap` in a corner of a 466pt-wide strip. `native-bridge` is abbreviated to
 * `bridge` because it is the only source whose name does not fit; the other three are already
 * short. A null edge prints `none` rather than nothing, so "no dock" and "the readout is broken"
 * cannot look the same.
 */
function DockReadout() {
  const dock = useSideDock();
  const fold = useFoldState();
  // The store's own spelling everywhere except the one that is too long for the badge.
  const source = fold.source === 'native-bridge' ? 'bridge' : fold.source;
  return (
    <span data-testid="dev-dock-readout">
      {source}/{dock.edge ?? 'none'}/{dock.housing}
    </span>
  );
}
